/**
 * useHederaWallet Hook
 *
 * Custom React hook for managing Hedera wallet data and state
 */

import { useQuery } from '@tanstack/react-query';
import { hederaAPI, tinybarToHbar } from '../services/hederaApi';
import type { HederaWalletData, HederaStakingReward, HederaNetworkStake } from '../types/hedera.types';

/**
 * Fetch HBAR price
 */
export const useHbarPrice = () => {
  return useQuery({
    queryKey: ['hedera', 'price'],
    queryFn: () => hederaAPI.getHbarPrice(),
    staleTime: 60_000, // 1 minute
    refetchInterval: 2 * 60_000, // Refetch every 2 minutes
  });
};

/**
 * Fetch network staking info
 */
export const useHederaNetworkStake = () => {
  return useQuery({
    queryKey: ['hedera', 'network', 'stake'],
    queryFn: () => hederaAPI.getNetworkStake(),
    staleTime: 5 * 60_000, // 5 minutes
    refetchInterval: 10 * 60_000, // Refetch every 10 minutes
  });
};

/**
 * Fetch wallet data for multiple accounts
 */
export const useHederaWallets = (accountIds: string[]) => {
  const { data: hbarPrice = 0 } = useHbarPrice();

  const {
    data: wallets,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['hedera', 'wallets', accountIds, hbarPrice],
    queryFn: () => hederaAPI.getMultipleWalletsData(accountIds, hbarPrice),
    enabled: accountIds.length > 0,
    staleTime: 60_000, // 1 minute
    refetchInterval: 5 * 60_000, // Refetch every 5 minutes
  });

  // Calculate aggregated metrics
  const totalBalance = wallets?.reduce((sum, w) => sum + w.balance, 0) || 0;
  const totalBalanceUsd = wallets?.reduce((sum, w) => sum + w.balanceUsd, 0) || 0;
  const totalPendingRewards = wallets?.reduce((sum, w) => sum + w.pendingReward, 0) || 0;
  const totalPendingRewardsUsd = wallets?.reduce((sum, w) => sum + w.pendingRewardUsd, 0) || 0;
  const totalRewardsReceived = wallets?.reduce((sum, w) => sum + w.totalRewardsReceived, 0) || 0;

  // Count unique tokens
  const uniqueTokens = new Set<string>();
  wallets?.forEach(w => w.tokens.forEach(t => uniqueTokens.add(t.tokenId)));

  // Count total NFTs
  const totalNFTs = wallets?.reduce((sum, w) => sum + w.nfts.length, 0) || 0;

  return {
    wallets: wallets || [],
    isLoading,
    error,
    refetch,
    hbarPrice,
    // Aggregated metrics
    totalBalance,
    totalBalanceUsd,
    totalPendingRewards,
    totalPendingRewardsUsd,
    totalRewardsReceived,
    totalRewardsReceivedUsd: totalRewardsReceived * hbarPrice,
    totalTokenTypes: uniqueTokens.size,
    totalNFTs
  };
};

/**
 * Fetch staking rewards history for an account
 */
export const useHederaRewards = (accountId: string) => {
  return useQuery({
    queryKey: ['hedera', 'rewards', accountId],
    queryFn: () => hederaAPI.getStakingRewards(accountId, 365), // Last year
    enabled: !!accountId,
    staleTime: 5 * 60_000, // 5 minutes
  });
};

/**
 * Calculate rewards statistics
 */
export const useHederaRewardsStats = (accountIds: string[], hbarPrice: number) => {
  const {
    data: allRewards,
    isLoading
  } = useQuery({
    queryKey: ['hedera', 'rewards', 'all', accountIds],
    queryFn: async () => {
      const results: HederaStakingReward[] = [];
      for (const id of accountIds) {
        const rewards = await hederaAPI.getStakingRewards(id, 365);
        results.push(...rewards);
      }
      return results;
    },
    enabled: accountIds.length > 0,
    staleTime: 5 * 60_000,
  });

  if (!allRewards || allRewards.length === 0) {
    return {
      isLoading,
      totalRewards: 0,
      totalRewardsUsd: 0,
      last7Days: 0,
      last30Days: 0,
      last90Days: 0,
      rewardsByDay: [] as { date: string; amount: number }[]
    };
  }

  const now = Date.now();
  const day7Ago = now - 7 * 24 * 60 * 60 * 1000;
  const day30Ago = now - 30 * 24 * 60 * 60 * 1000;
  const day90Ago = now - 90 * 24 * 60 * 60 * 1000;

  let totalRewards = 0;
  let last7Days = 0;
  let last30Days = 0;
  let last90Days = 0;
  const rewardsByDay = new Map<string, number>();

  for (const reward of allRewards) {
    const amountHbar = tinybarToHbar(reward.amount);
    const timestamp = hederaAPI.formatTimestamp(reward.timestamp).getTime();
    const dateStr = new Date(timestamp).toISOString().split('T')[0];

    totalRewards += amountHbar;
    rewardsByDay.set(dateStr, (rewardsByDay.get(dateStr) || 0) + amountHbar);

    if (timestamp >= day7Ago) last7Days += amountHbar;
    if (timestamp >= day30Ago) last30Days += amountHbar;
    if (timestamp >= day90Ago) last90Days += amountHbar;
  }

  // Convert to array and sort
  const rewardsByDayArray = Array.from(rewardsByDay.entries())
    .map(([date, amount]) => ({ date, amount }))
    .sort((a, b) => a.date.localeCompare(b.date));

  return {
    isLoading,
    totalRewards,
    totalRewardsUsd: totalRewards * hbarPrice,
    last7Days,
    last30Days,
    last90Days,
    last7DaysUsd: last7Days * hbarPrice,
    last30DaysUsd: last30Days * hbarPrice,
    last90DaysUsd: last90Days * hbarPrice,
    rewardsByDay: rewardsByDayArray
  };
};

/**
 * Get all tokens across all wallets
 */
export const useHederaTokens = (wallets: HederaWalletData[]) => {
  // Aggregate tokens from all wallets
  const tokenMap = new Map<string, {
    tokenId: string;
    symbol: string;
    name: string;
    totalBalance: number;
    decimals: number;
    type: 'FUNGIBLE_COMMON' | 'NON_FUNGIBLE_UNIQUE';
    walletCount: number;
  }>();

  for (const wallet of wallets) {
    for (const token of wallet.tokens) {
      const existing = tokenMap.get(token.tokenId);
      if (existing) {
        existing.totalBalance += token.balance;
        existing.walletCount++;
      } else {
        tokenMap.set(token.tokenId, {
          tokenId: token.tokenId,
          symbol: token.symbol,
          name: token.name,
          totalBalance: token.balance,
          decimals: token.decimals,
          type: token.type,
          walletCount: 1
        });
      }
    }
  }

  return Array.from(tokenMap.values()).sort((a, b) => b.totalBalance - a.totalBalance);
};

/**
 * Fetch token prices for a list of token IDs
 */
export const useHederaTokenPrices = (tokenIds: string[]) => {
  return useQuery({
    queryKey: ['hedera', 'tokenPrices', tokenIds],
    queryFn: () => hederaAPI.getTokenPrices(tokenIds),
    enabled: tokenIds.length > 0,
    staleTime: 5 * 60_000, // 5 minutes
    refetchInterval: 5 * 60_000, // Refetch every 5 minutes
  });
};

/**
 * Get all NFTs across all wallets
 */
export const useHederaNFTs = (wallets: HederaWalletData[]) => {
  const allNFTs = wallets.flatMap(w =>
    w.nfts.map(nft => ({
      ...nft,
      ownerAccountId: w.accountId
    }))
  );

  // Group by collection (token_id)
  const collections = new Map<string, typeof allNFTs>();
  for (const nft of allNFTs) {
    const existing = collections.get(nft.token_id) || [];
    existing.push(nft);
    collections.set(nft.token_id, existing);
  }

  return {
    allNFTs,
    collections: Array.from(collections.entries()).map(([tokenId, nfts]) => ({
      tokenId,
      count: nfts.length,
      nfts
    })),
    totalCount: allNFTs.length
  };
};
